import axios from 'axios';
import Toast from 'light-toast';
import { API_BASE_URL } from 'assets/constants/Constants';
import ShowToast from 'components/ShowToast';
import { TYPE_FAIL, INTERVAL } from 'assets/constants/Constants';

const GET = 'GET';
const POST = 'POST';
const PUT = 'PUT';
const DELETE = 'DELETE';
const PATCH = 'PATCH';

let lastToastTimestamp = 0;

const showToastOnce = (message, duration, errorCode) => {
    const currentTimestamp = Date.now();

    // Check if enough time has passed since the last toast
    if (currentTimestamp - lastToastTimestamp >= duration) {
        lastToastTimestamp = currentTimestamp;
        if (errorCode === 403 || errorCode === 401) {
            Toast.fail(message, 3000, () => {
                localStorage.clear();
                window.location.href = '/login-page';
            });
        } else {
            ShowToast(message, TYPE_FAIL, INTERVAL, '', '');
        }
    }
};


export const httpHandler = async (url, method, additionalHeaders, payload) => {
    const commonHeaders = {
        Accept: '*/*',
        'Content-Type': 'application/json',
    };

    if (url !== 'login') {
        commonHeaders['Authorization'] = `Bearer ${localStorage.getItem('JWToken')}`;
    }

    const headers = { ...commonHeaders, ...additionalHeaders };

    const axiosOptions = {
        method: method,
        headers: headers,
        url: `${API_BASE_URL}/${url}`,
    };

    if (method === GET && payload) {
        axiosOptions.params = payload;
    } else if (payload) {
        axiosOptions.data = JSON.stringify(payload);
    }
    try {
        if (navigator.onLine) {
            const resp = await axios(axiosOptions);
            const response = { statusCode: resp.status, data: await resp.data };

            switch (response.statusCode) {
                case 200:
                    return { ...response, status: 'SUCCESS' };
                case 500:
                    return {
                        ...response,
                        status: 'WARNING',
                        message: 'Something went wrong. Please try again later.',
                    };
                default:
                    return response;
            }
        } else {
            showToastOnce('No internet connection', INTERVAL);
            return { statusCode: 0, status: 'ERROR', message: 'No internet connection' };
        }
    } catch (error) {
        if (error.response) {
            switch (error.response.status) {
                case 403:
                    showToastOnce('Session expired. Redirecting to login page...', INTERVAL, 403);
                    break;
                case 401:
                    showToastOnce('Unauthorized. Redirecting to login page...', INTERVAL, 401);
                    break;
                case 404:
                    showToastOnce('Resource not found', INTERVAL, 404);
                    break;
                case 429:
                    showToastOnce('Too Many Requests. Please try again later.', INTERVAL, 429);
                    break;
                default:
                    showToastOnce('Network error', INTERVAL, null);
                    break;
            }
        } else if (error.request) {
            showToastOnce('Internal Server Error. Please try again later', INTERVAL, 500);
        } else {
            showToastOnce('Unexpected error', INTERVAL, null);
        }
        return { statusCode: 0, status: 'ERROR', message: 'Network error or other issues' };
    }
};
export const getProjectTotalForAdmin = async (
    additionalHeaders,
    body,
    projectType,
    selectedYear,
    selectedMonth
) => {
    const url = `getProjectTotalForAdmin/${projectType}/${selectedYear}/${selectedMonth}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getCandidateTotalForAdmin = async (
    additionalHeaders,
    body,
    selectedYear,
    selectedMonth
) => {
    const url = `getCandidateTotalForAdmin/${selectedYear}/${selectedMonth}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getEmployerList = async (additionalHeaders, body) => {
    const url = `employer/list`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getPartnerList = async (additionalHeaders, body) => {
    const url = `partner/list`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getCandidateList = async (additionalHeaders, body) => {
    const url = `candidate/list`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const deleteCandidate = async (additionalHeaders, body, candidateId) => {
    const url = `candidate/${candidateId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, body);
    return response;
};

export const createCandidateCall = async (additionalHeaders, body) => {
    const url = `candidate`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const updateCandidateCall = async (additionalHeaders, body, candidateID) => {
    const url = `candidate/${candidateID}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
};

export const getFieldData = async (additionalHeaders, body) => {
    const url = `datacapture/list`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const createFieldData = async (additionalHeaders, body) => {
    const url = `datacapture`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const updateFieldData = async (additionalHeaders, body, fieldId) => {
    const url = `datacapture/${fieldId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
};

export const deleteFieldData = async (additionalHeaders, body, fieldId) => {
    const url = `datacapture/${fieldId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, body);
    return response;
};

export const createStep = async (additionalHeaders, body) => {
    const url = `workflowsteps`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const updateStep = async (additionalHeaders, body) => {
    const url = `workflowsteps/${body.stepId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
};

export const getStepList = async (additionalHeaders, body) => {
    const url = `workflowsteps/list`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const deleteStep = async (additionalHeaders, body, stepId) => {
    const url = `workflowsteps/${stepId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, body);
    return response;
};

export const createEmployerCall = async (additionalHeaders, body) => {
    const url = `employer`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const updateEmployerCall = async (additionalHeaders, body, employerId) => {
    const url = `employer/${employerId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
};

export const deleteEmployer = async (additionalHeaders, body, employerId) => {
    const url = `employer/${employerId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, body);
    return response;
};

export const fetchEmployerTotalCall = async (additionalHeaders, body, employerId) => {
    const url = `employerDashboard/${employerId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const forgotPasswordCall = async (additionalHeaders, body) => {
    const url = `user/forgetPassword`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const getRoleCall = async (additionalHeaders, body, role) => {
    const url = `role/${role}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getMetaDataCall = async (additionalHeaders, body) => {
    const url = `getMetadata`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const updateQuotationMetadataCall = async (additionalHeaders, body) => {
    const url = `updateQuotationMetadata`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const createPartnerCall = async (additionalHeaders, body) => {
    const url = `partner`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const updatePartnerCall = async (additionalHeaders, body, partnerId) => {
    const url = `partner/${partnerId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
};

export const getPartnerTotalCall = async (additionalHeaders, body, partnerId) => {
    const url = `partnerDashboard/${partnerId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const deletePartner = async (additionalHeaders, body, partnerId) => {
    const url = `partner/${partnerId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, body);
    return response;
};

export const updateProgramCall = async (additionalHeaders, body, programId) => {
    const url = `program/${programId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
};

export const createProgramCall = async (additionalHeaders, body) => {
    const url = `program`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const fetchCandidateByCandidateIdArray = async (additionalHeaders, body) => {
    const url = `candidate/candidateArray`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const deleteProgramCall = async (additionalHeaders, body, programId) => {
    const url = `program/${programId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, body);
    return response;
};

export const getProgramListByEmployer = async (additionalHeaders, body, employerId) => {
    const url = `program/listByEmployer/${employerId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getProgramList = async (additionalHeaders, body) => {
    const url = `program/list`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getEmployerAndPartnerList = async (additionalHeaders, body) => {
    const url = `program/employerAndParnter/partnerlist`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getProgramData = async (additionalHeaders, body, programId) => {
    const url = `program/${programId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getCandidateByEmployer = async (additionalHeaders, body) => {
    const url = `candidate/byEmployer`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const getCandidatesByCandidateNames = async (additionalHeaders, body, programId) => {
    const url = `candidate/getCandidatesNames/${programId}`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const getStepListByStepIdArray = async (additionalHeaders, body) => {
    const url = `workflowsteps/getStepsForms`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const getProgramTrackerData = async (additionalHeaders, body, programId) => {
    const url = `programtracker/program/${programId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const createProgramTrackerData = async (additionalHeaders, body) => {
    const url = `programtracker`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const saveTrackerExcelImportData = async (additionalHeaders, body) => {
    const url = `programtracker/excelImportData`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const sendDropoutEmail = async (additionalHeaders, body) => {
    const url = `candidate/sendDroppedEmail`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const getRoleListCall = async (additionalHeaders, body) => {
    const url = `role`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const createRoleCall = async (additionalHeaders, body) => {
    const url = `role`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const updateRoleCall = async (additionalHeaders, body, roleId) => {
    const url = `role/${roleId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
};

export const deleteRole = async (additionalHeaders, body, roleId) => {
    const url = `role/${roleId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, body);
    return response;
};

export const findUserNameCall = async (additionalHeaders, body, event) => {
    const url = `user/findUserName/${event}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const createUserCall = async (additionalHeaders, body) => {
    const url = `user`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const UpdateUserCall = async (additionalHeaders, body, userId) => {
    const url = `user/${userId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
};

export const getUserList = async (additionalHeaders, body, event) => {
    const url = `user`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const deleteUserCall = async (additionalHeaders, body, userId) => {
    const url = `user/${userId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, body);
    return response;
};

export const refreshAccessTokenCall = async (additionalHeaders, body) => {
    const url = `refreshToken`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const createBulkCandidate = async (additionalHeaders, body) => {
    const url = `candidate/bulkCandidate`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const signInCall = async (additionalHeaders, body) => {
    const url = `login`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const getPartnerTotalProjectForAdmin = async (
    additionalHeaders,
    body,
    projectType,
    selectedYear,
    selectedMonth
) => {
    const url = `getPartnerTotalProjectForAdmin/${projectType}/${selectedYear}/${selectedMonth}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getEmployerTotalProjectForAdmin = async (
    additionalHeaders,
    body,
    projectType,
    selectedYear,
    selectedMonth
) => {
    const url = `getEmployerTotalProjectForAdmin/${projectType}/${selectedYear}/${selectedMonth}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getPermissionMatrixCall = async (additionalHeaders, body) => {
    const url = `permissionmatrix`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const createJobseeker = async (additionalHeaders, body) => {
    const url = `createJobseeker`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const checkEmailPresent = async (additionalHeaders, body, candidateEmail) => {
    const url = `candidate/isEmailPresent/${candidateEmail}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const checkAadharPresent = async (additionalHeaders, body, aadharNo) => {
    const url = `candidate/isAadharPresent/${aadharNo}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const resendEmailRegistration = async (additionalHeaders, body) => {
    const url = `candidate/resendRegistration`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const getProgramListByPartner = async (additionalHeaders, body, partnerId) => {
    const url = `program/listByPartner/${partnerId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getCandidateByPartner = async (additionalHeaders, body) => {
    const url = 'candidate/byPartner';
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const getStepData = async (additionalHeaders, body, stepId) => {
    const url = `workflowsteps/${stepId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getTrackerDataByDifferentIds = async (additionalHeaders, body) => {
    const url = `programtracker/gettrackerdata`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const getCandidateById = async (additionalHeaders, body, candidateId) => {
    const url = `candidate/${candidateId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const updateProgramTrackerData = async (additionalHeaders, body, trackerId) => {
    const url = `programtracker/${trackerId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
};

export const getProgramCandidateList = async (additionalHeaders, body, programId) => {
    const url = `candidate/program/list/${programId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const createBulkEmployer = async (additionalHeaders, body) => {
    const url = `employer/bulkcreate`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const getBulkCandidateByAadhaarArray = async (additionalHeaders, body) => {
    const url = `candidate/aadhaar`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const createBulkPartner = async (additionalHeaders, body) => {
    const url = `partner/bulkimport`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const candidateInterviewFailedUpdate = async (additionalHeaders, body) => {
    const url = `candidate/candidateInterviewFailedUpdate`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const candidateSelectFromFailedUpdate = async (additionalHeaders, body) => {
    const url = `candidate/candidateSelectFromFailedUpdate`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const resetPasswordByOtpCall = async (additionalHeaders, body) => {
    const url = `user/resetpassword/otp`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
};

export const resetPasswordByAdminCall = async (additionalHeaders, body) => {
    const url = `user/reset/password/by/admin`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
};

export const getUserDetails = async (additionalHeaders, body, userId) => {
    const url = `user/${userId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const fetchProgramByProgramIdArray = async (additionalHeaders, body) => {
    const url = `program/getProgramDataByIdBatch`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const getUserDataByIdBatch = async (additionalHeaders, body) => {
    const url = `user/getUserDataByIdBatch`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const getTrackingHistoryByUserId = async (additionalHeaders, body, userId) => {
    const url = `userTrackingHistory/list/${userId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getTrackingHistoryByProgramId = async (additionalHeaders, body, programId) => {
    const url = `userTrackingHistory/list/byprogram/${programId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getCandidateHistoryByCandidateId = async (additionalHeaders, body, candidateId) => {
    const url = `candidateHistory/candidate/${candidateId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getYtdProjectTotalForAdmin = async (
    additionalHeaders,
    body,
    projectType,
    selectedYear,
    selectedMonth
) => {
    const url = `getProjectTotalForAdminByYTD/${projectType}/${selectedYear}/${selectedMonth}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getYtdCandidateTotalForAdmin = async (
    additionalHeaders,
    body,
    selectedYear,
    selectedMonth
) => {
    const url = `getCandidateTotalForAdminByYTD/${selectedYear}/${selectedMonth}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const revokeDropoutStatus = async (additionalHeaders, body) => {
    const url = `programtracker/revoke/dropout`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const getProjectwiseAdminData = async (
    additionalHeaders,
    body,
    projectType,
    selectedYear,
    selectedMonth
) => {
    const url = `getProjectTotalForAdmin/projectwise/${projectType}/${selectedYear}/${selectedMonth}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getS3PresignedUrl = async (additionalHeaders, body) => {
    const url = `generate-presigned-url`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const uploadDocumentBucket = async (additionalHeaders, body) => {
    const { url, file } = body;
    const response = await axios.put(url, file, { headers: additionalHeaders });
    return response;
};

export const downloadDocumentBucket = async (additionalHeaders, body) => {
    const url = `generate-download-url`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const createProjectAttendance = async (additionalHeaders, body) => {
    const url = `project-attendance`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const getProjectAttendanceByProgramId = async (additionalHeaders, body, programId) => {
    const url = `project-attendance/program/${programId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const updateProjectAttendance = async (additionalHeaders, body, projectAttendanceId) => {
    const url = `project-attendance/${projectAttendanceId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
};

export const createBulkCandidateAttendance = async (additionalHeaders, body) => {
    const url = `candidate-attendance/bulk/attendance`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const getCandidateAttendanceByProgramId = async (additionalHeaders, body, programId) => {
    const url = `candidate-attendance/program/${programId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getCandidateListByProgramId = async (
    additionalHeaders,
    body,
    programId,
    partnerId
) => {
    const url = `candidate/candidate-list/program/${programId}/${partnerId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getPartnerById = async (additionalHeaders, body, partnerId) => {
    const url = `partner/${partnerId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const searchCandidateInTP = async (additionalHeaders, body) => {
    const url = `partner/searchCandidateInTP`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const updateAttendance = async (additionalHeaders, body, candidateID) => {
    const url = `candidate-attendance/mark-attendance/${candidateID}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
};

export const fetchAttendanceReportByProgramId = async (additionalHeaders, body, programId) => {
    const url = `candidate-attendance/generate/attendance-report/${programId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const updateCandidateImportOrder = async (additionalHeaders, body) => {
    const url = `program/updateCandidateImportOrder`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const captureLocationCall = async (additionalHeaders, body) => {
    const url = `partner/captureLocation`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const updatePartnerApprovalStatus = async (additionalHeaders, body, partnerId) => {
    const url = `partner/approve/${partnerId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
};

export const findUserByPartner = async (additionalHeaders, body, partnerId) => {
    const url = `user/finduserpartner/${partnerId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const removeCandidate = async (additionalHeaders, body) => {
    const url = `candidate/remove`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const createProjectAttendanceByAdmin = async (additionalHeaders, body) => {
    const url = `project-attendance/create/by-admin`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const updateProjectAttendanceByAdmin = async (
    additionalHeaders,
    body,
    projectAttendanceId
) => {
    const url = `project-attendance/update/by-admin/${projectAttendanceId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
};

export const getProjectwiseCountData = async (additionalHeaders, body, projectId) => {
    const url = `getProjectTotalForAdmin/projectwisedata/${projectId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getProgramListCSRPlacement = async (additionalHeaders, body) => {
    const url = `program/list/active/non/csr`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getCandidateOriginByNewProgramId = async (additionalHeaders, body, programId) => {
    const url = `candidateHistory/getCandidateOriginByNewProgramId/${programId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

// --- Enquiry API Methods ---
export const createEnquiry = async (additionalHeaders, body) => {
    const url = `enquiries`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

/*export const getEnquiryList = async (additionalHeaders, body) => {
    const url = `enquiries`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};*/
export const getEnquiryList = async (additionalHeaders = {}, queryParams = {}) => {
    const url = 'enquiries';
    const response = await httpHandler(url, GET, additionalHeaders, queryParams);
    return response;
};

export const getEnquiryById = async (additionalHeaders, body, enquiryId) => {
    const url = `enquiries/${enquiryId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};
export const deleteEnquiry = async (additionalHeaders, enquiryId) => {
    const url = `enquiries/${enquiryId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, {});
    return response;
};

export const updateEnquiry = async (additionalHeaders, body, enquiryId) => {
    const url = `enquiries/${enquiryId}`;
    const response = await httpHandler(url, PATCH, additionalHeaders, body);
    return response;
};


export const getCurrentCandidateCSRotherplacement = async (additionalHeaders, body) => {
    const url = `candidateHistory/current-candidate-csr-otherplacement`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

//stdProcess
export const createStdProcess = async (additionalHeaders, body) => {
    const url = `stdprocess`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const updateStdProcess = async (additionalHeaders, body, stdProcessId) => {
    const url = `stdprocess/${stdProcessId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
};

export const getStdProcessList = async (additionalHeaders, body) => {
    const url = `stdprocess`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getStdProcessById = async (additionalHeaders, body, stdProcessId) => {
    const url = `stdprocess/${stdProcessId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const deleteStdProcess = async (additionalHeaders, body, stdProcessId) => {
    const url = `stdprocess/${stdProcessId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, body);
    return response;
};

//Customers
export const getCustomerList = async (additionalHeaders, body) => {
    const url = `customers`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const deleteCustomer = async (additionalHeaders, body, customerId) => {
    const url = `customers/${customerId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, body);
    return response;
};

export const createCustomer = async (additionalHeaders, body) => {
    const url = `customers`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const updateCustomer = async (additionalHeaders, body, customerId) => {
    const url = `customers/${customerId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
};
export const getCustomerById = async (additionalHeaders, body, customerId) => {
    const url = `customers/${customerId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};
//Bom
export const createBOM = async (additionalHeaders, body) => {
    const url = `boms`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const getBomList = async (additionalHeaders, body) => {
    const url = `boms`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};
export const getBomById = async (additionalHeaders, body, bomId) => {
    const url = `boms/${bomId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const updateBOM = async (additionalHeaders, body, bomId) => {
    const url = `boms/${bomId}`;
    const response = await httpHandler(url, PATCH, additionalHeaders, body);
    return response;
};

export const pauseBom = async (additionalHeaders, bomId) => {
    const url = `boms/${bomId}/pause`;
    const response = await httpHandler(url, POST, additionalHeaders, {});
    return response;
};

export const resumeBom = async (additionalHeaders, bomId) => {
    const url = `boms/${bomId}/resume`;
    const response = await httpHandler(url, POST, additionalHeaders, {});
    return response;
};

export const deleteBom = async (additionalHeaders, body, bomId) => {
    const url = `boms/${bomId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, body);
    return response;
};

export const getBomsByStage = async (additionalHeaders, params) => {
    const url = `boms/stage`;
    const response = await httpHandler(url, GET, additionalHeaders, params);
    return response;
};

export const updateBomItemPartial = async (additionalHeaders, bomId, itemId, partialBody) => {
    const url = `boms/${bomId}/items/${itemId}/partial-update`;
    return httpHandler(url, PATCH, additionalHeaders, partialBody);
};

export const rejectBomItem = async (additionalHeaders, bomId, itemId, body) => {
    const url = `bom/reject/${bomId}/${itemId}`;
    return httpHandler(url, PUT, additionalHeaders, body);
};
export const getPOListForBom = async (additionalHeaders = {}, body = {}) => {
    const url = `boms/po-list`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getPODetailsForBom = async (additionalHeaders = {}, body = {}, poId) => {
    const url = `boms/po-details/${poId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};
//Business Vertical
export const createBusinessVertical = async (additionalHeaders, body) => {
    const url = `business-verticals`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};
export const getBusinessVerticalList = async (additionalHeaders, body) => {
    const url = `business-verticals`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};
export const updateBusinessVertical = async (additionalHeaders, body, businessVerticalId) => {
    const url = `business-verticals/${businessVerticalId}`;
    const response = await httpHandler(url, PATCH, additionalHeaders, body);
    return response;
};
export const deleteBusinessVertical = async (additionalHeaders, body, businessVerticalId) => {
    const url = `business-verticals/${businessVerticalId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, body);
    return response;
};
export const getBusinessVerticalById = async (additionalHeaders, body, businessVerticalId) => {
    const url = `business-verticals/${businessVerticalId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};
//Region
export const createRegion = async (additionalHeaders, body) => {
    const url = `regions`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};
export const getRegionList = async (additionalHeaders, body) => {
    const url = `regions`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};
export const getRegionById = async (additionalHeaders, body, regionId) => {
    const url = `regions/${regionId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};
export const updateRegion = async (additionalHeaders, body, regionId) => {
    const url = `regions/${regionId}`;
    const response = await httpHandler(url, PATCH, additionalHeaders, body);
    return response;
};

export const deleteRegion = async (additionalHeaders, body, regionId) => {
    const url = `regions/${regionId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, body);
    return response;
};

//Component Parts

export const getPartList = async (additionalHeaders, body) => {
    const url = `parts`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const createParts = async (additionalHeaders, body) => {
    const url = `parts`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const updatePart = async (additionalHeaders, body, partId) => {
    const url = `parts/${partId}`;
    const response = await httpHandler(url, PUT, additionalHeaders, body);
    return response;
};
export const deletePart = async (additionalHeaders, body, partId) => {
    const url = `parts/${partId}`;
    const response = await httpHandler(url, DELETE, additionalHeaders, body);
    return response;
};
export const getDocNumberForPart = async (additionalHeaders, body, partId, fixtureDocNo) => {
    const url = `parts/${partId}/doc-number?fixture_document_number=${fixtureDocNo}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};


export const getRawmaterialByOdandMaterial = async (additionalHeaders, od, steelMaterial) => {
  if (od === undefined || steelMaterial === undefined) {
    throw new Error('Both "od" and "steelMaterial" are required');
  }

  const params = new URLSearchParams({
    od: String(od),
    steelMaterial: String(steelMaterial),
  });

  const url = `parts/by-od-and-material?${params.toString()}`;
  const response = await httpHandler(url, GET, additionalHeaders, null);
  return response;
};

export const searchPartsByDocNo = async (additionalHeaders, body) =>{
    const url = `parts/search-by-doc`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

////
export const updatestepfrombom = async (additionalHeaders, bomId, itemId, stepRefId, body) => {
    const url = `boms/${bomId}/items/${itemId}/steps/${stepRefId}`;
    const response = await httpHandler(url, PATCH, additionalHeaders, body);
    return response;
};

export const updateProcurementStatus = async (additionalHeaders, bomId, itemId, body) => {
    const url = `boms/${bomId}/items/${itemId}`;
    const response = await httpHandler(url, PATCH, additionalHeaders, body);
    return response;
};

export const getPartById = async (additionalHeaders, body, partId) => {
    const url = `parts/${partId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

//Audit Trail Page
export const getHistoryList = async (additionalHeaders, body, event) => {
    const url = `auditlogs/list`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const createHistoryList = async (additionalHeaders, body) => {
    const url = `auditlogs`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

  export const updateItemProcurement = async(additionalHeaders, bomId, itemId, body) =>{
    const url = `boms/${bomId}/items/${itemId}/procurement`;
    const response = await httpHandler(url, PATCH, additionalHeaders, body);
    return response;
  } 

export const getUserByRole = async (additionalHeaders, body, role) => {
    const url = `user-by-role`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const splitBomItemToPlanning = async (additionalHeaders, bomId, itemId) => {
  const url = `boms/${bomId}/items/${itemId}/split-to-planning`;
  const response = await httpHandler(url, POST, additionalHeaders, null);
  return response;
};

export const dashboardData = async (additionalHeaders, body) => {
  const url = `boms/analytics`;
  const response = await httpHandler(url, GET, additionalHeaders, body);
  return response;
};

export const getBomHistoryLogs = async (additionalHeaders,body, bomid ) =>{
    const url = `auditlogs/history/${bomid}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;

}

// Delivery Trail API Endpoints

export const createDeliveryTrail = async (additionalHeaders, body) => {
    const url = `delivery-trails`;
    const response = await httpHandler(url, POST, additionalHeaders, body);
    return response;
};

export const getAllDeliveryTrails = async (additionalHeaders, body) => {
    const url = `delivery-trails`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const getDeliveryTrailByBomId = async (additionalHeaders, bomId) => {
    const url = `delivery-trails/${bomId}`;
    const response = await httpHandler(url, GET, additionalHeaders, null);
    return response;
};

export const updateDeliveryStep = async (additionalHeaders, bomId, stepName, body) => {
    const url = `delivery-trails/${bomId}/steps/${stepName}`;
    const response = await httpHandler(url, PATCH, additionalHeaders, body);
    return response;
};


//download attachmets as zip
export const downloadAllBomAttachments = async (bomId) => {
    try {
        const response = await axios({
            url: `${API_BASE_URL}/boms/${bomId}/download-attachments`,
            method: 'GET',
            responseType: 'blob',
            timeout: 300000, 
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('JWToken')}`
            }
        });


        if (response.data.type === 'application/json') {
            const text = await response.data.text();
            const errorJson = JSON.parse(text);
            throw new Error(errorJson.error || 'Download failed');
        }

        return response;
    } catch (error) {
        console.error("Download Zip Error", error);
        throw error;
    }
};

export const downloadDispatchAttachments = async (bomId) => {
    try {
        const response = await axios({
            url: `${API_BASE_URL}/boms/${bomId}/download-dispatch-attachments`,
            method: 'GET',
            responseType: 'blob',
            timeout: 300000, 
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('JWToken')}`
            }
        });


        if (response.data.type === 'application/json') {
            const text = await response.data.text();
            const errorJson = JSON.parse(text);
            throw new Error(errorJson.error || 'Download failed');
        }

        return response;
    } catch (error) {
        console.error("Download Zip Error", error);
        throw error;
    }
};

export const getBomFormOptions = async (additionalHeaders, body) => {
    const url = `boms/form-options`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const chatAssistantCall = async (additionalHeaders, body) => {
    const url = 'bom/chat-assistant';
    const response = await httpHandler(url, POST, additionalHeaders, body);


};

// PO API Methods
export const getPoList = async (additionalHeaders = {}, queryParams = {}) => {
    const url = 'purchase-orders';
    const response = await httpHandler(url, GET, additionalHeaders, queryParams);
    return response;
};

export const getPoById = async (additionalHeaders, body, poId) => {
    const url = `purchase-orders/${poId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const createPurchaseOrder = async (body) => {
    const url = 'convert-enquiry-to-po';
    const response = await httpHandler(url, POST, {}, body);
    return response;
};

export const getPoByEnquiryId = async (additionalHeaders = {}, body = {}, enquiryId) => {
    const url = `purchase-orders/enquiry/${enquiryId}`;
    const response = await httpHandler(url, GET, additionalHeaders, body);
    return response;
};

export const updatePurchaseOrder = async (poId, body) => {
    const url = `purchase-orders/${poId}`;
    const response = await httpHandler(url, PUT, {}, body);
    return response;
};